import { User } from "firebase/auth";
import { DocumentReference, Timestamp } from "firebase/firestore";
import type { TwilioError } from "twilio-video";

export type ErrorCallback = (error: TwilioError | Error) => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic callback
export type Callback = (...args: any[]) => void;

export enum UserRole {
  Student = "student",
  Tutor = "tutor",
}

export interface Subject {
  name: string;
  specialties: string[];
}

export interface Diagram {
  id: string;
  title: string;
  concept: string;
  imageUrl: string;
  description: string;
  isApproved: boolean;
  hasStudentRead: boolean;
  hasTutorRead: boolean;
}

export interface Example {
  id: string;
  concept: string;
  title: string;
  description: string;
  isApproved: boolean;
  hasStudentRead: boolean;
  hasTutorRead: boolean;
  isGenerated: boolean;
}

export interface Problem {
  id: string;
  title: string;
  question: string;
  correctAnswer: string;
  studentAnswer?: string;
  score: number;
  points: number;
  options: string[];
  topic: string;
  subject: string;
  solution: string;
  type: QuestionType;
  isGenerated: boolean;
  isApproved: boolean;
  hasStudentRead: boolean;
  hasTutorRead: boolean;
  formatVersion?: number;
}

export enum QuestionType {
  MultipleChoice = "multiple-choice",
  TrueFalse = "true-false",
  ShortAnswer = "short-answer",
}

export enum LunaTabName {
  Problems = "problems",
  Solutions = "solutions",
  Examples = "examples",
  Diagrams = "diagrams",
  StudentProfile = "student profile",
  LessonPlan = "lesson plan",
}

export interface Lesson {
  id: string;
  bookingId: string;
  confirmationReminderSent: boolean;
  duration: number;
  examPreparation: string[];
  hasCompleted: boolean;
  hasStarted: boolean;
  introDuration: number;
  isCancelled: boolean;
  isConfirmed: boolean;
  note: string;
  recordingEnabled: boolean;
  recordingId: string | null;
  roomId: string;
  startDateTime: Timestamp;
  studentId: string;
  studentName: string;
  studentPhotoUrl: string;
  subjects: Subject[];
  tutorId: string;
  tutorName: string;
  tutorPhotoUrl: string;
  type: string;
}

export interface LessonInteractionProfile {
  id: string;
  lessonId: string;
  studentId: string;
  tutorId: string;
  subjects: Subject[];
  currentRelevantTopic: Topic;
  duration: number;
  note: string;
  recordingId: string;
  realtimeResources: RealtimeResources;
  transcript?: string;
}

export interface Topic {
  subject: string;
  specialty: string;
  topic: string;
}
export interface RealtimeResources {
  problems: Problem[];
  examples: Example[];
  diagrams: Diagram[];
}

export interface LessonPlan {
  id: string;
  title: string;
  overview: string;
  objectives: string[];
  totalDuration: string;
  steps: LessonStep[];
  lessonId: string;
  subjects: Subject[];
  gradeLevel: string;
  duration: string;
  createdAt: Timestamp;
  createdBy: string;
  isFavorited: boolean;
  status: "Generating" | "Error" | "Generated";
}

export interface LessonStep {
  title: string;
  duration: string;
  content: string;
}
export interface Differentiation {
  advancedStudents: string;
  strugglingStudents: string;
  ell: string;
}

export interface LessonResourceState {
  notifiedItems: string[];
  readItems: string[];
}

export interface Recording {
  id: string;
  processedAt: Timestamp;
  recordingUrl: string;
  roomSId: string;
  status: string;
  trackCount: {
    audio: number;
    video: number;
    duration: number;
  };
}

export interface Student extends User {
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  stripe: {
    customerId: string;
  };
  favourites: DocumentReference[];
  photoUrl: string;
  notificationPreferences: NotificationPreferences;
  learningProfile: LearningProfile;
}

export interface LearningProfile {
  preferredName: string;
  subjects: Subject[];
  gender: string;
  gradeLevel: string;
  schoolName: string;
  programMajor: string;
  curriculumRegion: string;
  preferredLanguage: string;
  secondaryLanguages: string[];
  idealBreak: string;
  interests: string[];
  dateOfBirth: Timestamp;
  goals: string[];
  longTermGoals: string[];
  pace: string;
  contentFormats: string[];
  learningStyle: string;
  disabilities: string[];
  challenges: string;
  accessibility: string[];
  assistiveTech: string;
  academicStrengths: string[];
  areasForImprovement: string[];
  confidenceLevel: string;
  studyHabits: string[];
  motivationFactors: string[];
}

export interface NotificationPreferences {
  accountUpdates: boolean;
  newMessages: boolean;
  offersAndUpdates: boolean;
}
